import type AccessibilityTtsVoiceEnum from "@shared-enums/accessibility/accessibility.tts.voice.enum";
import type TtsEngineInterface from "@shared-interfaces/accessibility/tts.engine.interface";

/** Indices de genre dans le nom d'une voix : le navigateur ne le déclare pas. */
const MASCULINE_INDICATORS = [
	// Indicateurs génériques
	"male", "homme", "grandpa", "grandfather",
	// Prénoms masculins français
	"thomas", "nicolas", "pierre", "jean", "paul", "daniel", "david", "fred", "jacques",
	"antoine", "louis", "gabriel", "hugo", "lucas", "mathis", "nathan", "theo", "raphael",
	// Prénoms masculins anglais (voix Edge/macOS)
	"eddy", "rocko", "reed", "alex", "bruce", "charles", "eric", "george", "james", "john",
	"mark", "michael", "richard", "robert", "william", "albert", "arthur", "henry"
];

const FEMININE_INDICATORS = [
	// Indicateurs génériques
	"female", "femme", "grandma", "grandmother",
	// Prénoms féminins français
	"amélie", "amelie", "aurelie", "marie", "audrey", "virginie", "julie", "clara",
	"alice", "emma", "lea", "sophie", "camille", "chloe", "manon", "sarah", "laura",
	"denise", "sylvie",
	// Prénoms féminins anglais (voix Edge/macOS)
	"samantha", "victoria", "karen", "moira", "tessa", "flo", "sandy", "shelley",
	"susan", "lisa", "jennifer", "emily", "jessica", "ashley", "amanda", "sarah"
];

/**
 * La synthèse vocale du navigateur : le web, l'admin, l'app coach et l'app adhérent sur iOS.
 *
 * Seul Android lui échappe, sa WebView n'exposant pas `speechSynthesis`. Elle reste préférée sur
 * iOS parce qu'elle suit les mots prononcés et distingue les voix, deux choses que le plugin
 * natif n'y sait pas faire.
 */
class WebIosTtsEngine implements TtsEngineInterface
{
	/** Les voix arrivent de façon asynchrone sur Chrome : on garde la meilleure de chaque genre. */
	private frenchVoiceFeminine: SpeechSynthesisVoice | null = null;
	private frenchVoiceMasculine: SpeechSynthesisVoice | null = null;
	private voiceGender: keyof typeof AccessibilityTtsVoiceEnum = "accessibility-tts-voice-feminine";

	/** Chrome interrompt une lecture longue au bout de quelques secondes : on la relance. */
	private resumeInterval: number | null = null;

	public constructor()
	{
		if (typeof window === "undefined" || ! window.speechSynthesis)
		{
			return;
		}

		// Les voix peuvent être chargées de manière asynchrone (surtout sur Chrome)
		if (window.speechSynthesis.onvoiceschanged !== undefined)
		{
			window.speechSynthesis.onvoiceschanged = () =>
			{
				this.frenchVoiceFeminine = null;
				this.frenchVoiceMasculine = null;
				this.getFrenchVoice();
			};
		}

		// Essaie de charger immédiatement (fonctionne sur Firefox/Safari)
		this.getFrenchVoice();
	}

	public setVoiceGender(gender: keyof typeof AccessibilityTtsVoiceEnum): void
	{
		if (this.voiceGender === gender)
		{
			return;
		}

		this.voiceGender = gender;
		this.frenchVoiceFeminine = null;
		this.frenchVoiceMasculine = null;
	}

	public speak(
		text: string,
		rate: number,
		callbacks?: {
			onStart?: () => void,
			onBoundary?: (charIndex: number) => void,
			onEnd?: () => void,
		}
	): void
	{
		if (! window.speechSynthesis)
		{
			return;
		}

		window.speechSynthesis.cancel();

		const utterance = new SpeechSynthesisUtterance(text);
		utterance.lang = "fr-FR";
		utterance.rate = rate;

		const voice = this.getFrenchVoice();
		if (voice)
		{
			utterance.voice = voice;
		}

		utterance.onstart = () => callbacks?.onStart?.();

		utterance.onboundary = (event) =>
		{
			if (event.name === "word")
			{
				callbacks?.onBoundary?.(event.charIndex);
			}
		};

		const finish = () =>
		{
			this.stopResumeWorkaround();
			callbacks?.onEnd?.();
		};

		utterance.onend = finish;
		utterance.onerror = finish;

		window.speechSynthesis.speak(utterance);
		this.startResumeWorkaround();
	}

	public stop(): void
	{
		window.speechSynthesis?.cancel();
		this.stopResumeWorkaround();
	}

	/**
	 * Détecte si une voix est féminine basé sur son nom
	 * Heuristique : noms féminins courants ou indicateurs
	 */
	private isFeminineVoice(voice: SpeechSynthesisVoice): boolean
	{
		const name = voice.name.toLowerCase();

		// Vérifie d'abord les indicateurs masculins
		for (const indicator of MASCULINE_INDICATORS)
		{
			if (name.includes(indicator) && ! name.includes("female"))
			{
				return false;
			}
		}

		// Vérifie les indicateurs féminins
		for (const indicator of FEMININE_INDICATORS)
		{
			if (name.includes(indicator))
			{
				return true;
			}
		}

		// Par défaut, considérer comme féminin (la plupart des voix par défaut le sont)
		return true;
	}

	/**
	 * Trouve la meilleure voix française disponible selon le genre
	 * Priorité : voix fr-FR native > voix fr-FR > voix fr-* > null
	 */
	private findBestFrenchVoice(voices: SpeechSynthesisVoice[], preferFeminine: boolean = true): SpeechSynthesisVoice | null
	{
		// Filtre les voix françaises
		const frenchVoices = voices.filter(v => v.lang === "fr-FR" || v.lang.startsWith("fr"));

		// Trie par priorité : natives fr-FR d'abord, puis fr-FR, puis autres
		const sortedVoices = frenchVoices.sort((a, b) =>
		{
			// Priorité aux voix natives
			if (a.localService && ! b.localService)
			{
				return -1;
			}
			if (! a.localService && b.localService)
			{
				return 1;
			}
			// Priorité à fr-FR
			if (a.lang === "fr-FR" && b.lang !== "fr-FR")
			{
				return -1;
			}
			if (a.lang !== "fr-FR" && b.lang === "fr-FR")
			{
				return 1;
			}
			return 0;
		});

		// Cherche une voix du bon genre
		for (const voice of sortedVoices)
		{
			const isFeminine = this.isFeminineVoice(voice);
			if ((preferFeminine && isFeminine) || (! preferFeminine && ! isFeminine))
			{
				return voice;
			}
		}

		// Si pas de voix du bon genre, retourner la première disponible
		return sortedVoices[0] || null;
	}

	/**
	 * Récupère la voix française selon le genre configuré
	 */
	private getFrenchVoice(): SpeechSynthesisVoice | null
	{
		const preferFeminine = this.voiceGender === "accessibility-tts-voice-feminine";

		// Vérifie le cache
		const cachedVoice = preferFeminine ? this.frenchVoiceFeminine : this.frenchVoiceMasculine;
		if (cachedVoice)
		{
			return cachedVoice;
		}

		const voices = window.speechSynthesis.getVoices();

		// Si les voix ne sont pas encore chargées, retourner null
		if (voices.length === 0)
		{
			return null;
		}

		// Charge les deux types de voix en cache
		this.frenchVoiceFeminine = this.findBestFrenchVoice(voices, true);
		this.frenchVoiceMasculine = this.findBestFrenchVoice(voices, false);

		return preferFeminine ? this.frenchVoiceFeminine : this.frenchVoiceMasculine;
	}

	private startResumeWorkaround(): void
	{
		this.stopResumeWorkaround();

		this.resumeInterval = window.setInterval(() =>
		{
			if (window.speechSynthesis.speaking && ! window.speechSynthesis.paused)
			{
				window.speechSynthesis.pause();
				window.speechSynthesis.resume();
			}
		}, 10000);
	}

	private stopResumeWorkaround(): void
	{
		if (this.resumeInterval)
		{
			clearInterval(this.resumeInterval);
			this.resumeInterval = null;
		}
	}
}

export default WebIosTtsEngine;
